import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "autexio_cookie_consent";

/**
 * Simple, elegant bottom cookie consent banner shown on first visit.
 * Stores consent in localStorage. Purely informational for now — no tracking
 * tools to gate. Link to /privacy for full policy.
 */
export default function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Delay a tick so the entrance animation is visible after route mount.
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const timer = setTimeout(() => setVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const accept = () => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ accepted: true, at: new Date().toISOString() })
    );
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          data-testid="cookie-banner"
          className="fixed bottom-3 left-3 right-3 md:left-6 md:right-auto md:bottom-6 md:max-w-md z-50 rounded-2xl bg-[#0A0A0B] text-white shadow-2xl shadow-black/25 p-4 md:p-5 border border-white/10"
          role="dialog"
          aria-live="polite"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center shrink-0">
              <Cookie className="w-4 h-4 text-blue-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] leading-snug">
                <span className="font-semibold">{t("cookies.title")}</span>{" "}
                <span className="text-slate-300">{t("cookies.body")}</span>{" "}
                <Link
                  to="/privacy"
                  data-testid="cookie-banner-privacy-link"
                  className="text-blue-300 hover:text-blue-200 underline underline-offset-2"
                >
                  {t("cookies.privacy_link")}
                </Link>
                .
              </p>
              <div className="mt-3">
                <button
                  type="button"
                  data-testid="cookie-banner-accept"
                  onClick={accept}
                  className="inline-flex items-center gap-2 rounded-full bg-white text-slate-950 px-4 py-1.5 text-xs font-semibold hover:bg-slate-100 transition-colors"
                >
                  {t("cookies.accept")}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
